export interface IncomingCallMatch {
  geschaeftspartner_id: number;
  typ: string;
  kind: "firma" | "ansprechpartner";
  label: string;
  firmenname: string;
  person_name: string;
  phone: string;
}

export interface IncomingCallNotice {
  phone: string;
  phone_display: string;
  matches: IncomingCallMatch[];
}

function parseMatch(raw: unknown): IncomingCallMatch | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = Number(row.geschaeftspartner_id);
  const label = String(row.label || "").trim();
  if (!Number.isFinite(id) || id <= 0 || !label) return null;
  const kind = row.kind === "ansprechpartner" ? "ansprechpartner" : "firma";
  return {
    geschaeftspartner_id: id,
    typ: String(row.typ || "").trim().toLowerCase(),
    kind,
    label,
    firmenname: String(row.firmenname || "").trim(),
    person_name: String(row.person_name || "").trim(),
    phone: String(row.phone || "").trim(),
  };
}

export function incomingCallFromPayload(data: unknown): IncomingCallNotice | null {
  if (!data || typeof data !== "object") return null;
  const row = data as Record<string, unknown>;
  const phone = String(row.phone || "").trim();
  const phoneDisplay = String(row.phone_display || "").trim();
  if (!phone && !phoneDisplay) return null;
  const matches = Array.isArray(row.matches)
    ? row.matches.flatMap((item) => {
        const match = parseMatch(item);
        return match ? [match] : [];
      })
    : [];
  return {
    phone,
    phone_display: phoneDisplay || phone,
    matches,
  };
}

export function contactPathForMatch(match: IncomingCallMatch): string | null {
  const id = Number(match.geschaeftspartner_id);
  if (!Number.isFinite(id) || id <= 0) return null;
  if (match.typ === "kunde") return `/kontakte/kunden/${id}`;
  if (match.typ === "lieferant") return `/kontakte/lieferanten/${id}`;
  return null;
}

/**
 * Liest `tel` oder `phone` aus der Query, ohne ein führendes `+` in ein Leerzeichen
 * zu verwandeln. Teams hängt die E.164-Nummer als `{phone}` an.
 */
export function readCallerPhone(search: string): string {
  const query = search.startsWith("?") ? search.slice(1) : search;
  if (!query) return "";
  for (const part of query.split("&")) {
    const eq = part.indexOf("=");
    if (eq <= 0) continue;
    let key = part.slice(0, eq);
    try {
      key = decodeURIComponent(key);
    } catch {
      /* Rohwert behalten */
    }
    if (key !== "tel" && key !== "phone") continue;
    const encoded = part.slice(eq + 1).replace(/\+/g, "%2B");
    try {
      return decodeURIComponent(encoded).trim();
    } catch {
      return encoded.trim();
    }
  }
  return "";
}

export function incomingCallSourceSessionId(data: unknown): string {
  if (!data || typeof data !== "object") return "";
  return String((data as { source_session_id?: unknown }).source_session_id || "").trim();
}
